import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiSend, FiArrowLeft, FiX } from 'react-icons/fi';
import api from '../services/api';

const ParentMessagesManagePage = () => {
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState('open');
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyBody, setReplyBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadThreads = () => {
    setLoading(true);
    api.get('/parent-portal/messages-manage', { params: filter ? { status: filter } : {} })
      .then((res) => { if (res.data.success) setThreads(res.data.data); })
      .catch(() => toast.error('Erreur'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadThreads(); }, [filter]);

  const openThread = async (id) => {
    try {
      const res = await api.get(`/parent-portal/messages-manage/${id}`);
      if (res.data.success) {
        setActiveThread(res.data.thread);
        setMessages(res.data.messages);
        loadThreads();
      }
    } catch {
      toast.error('Erreur');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !activeThread) return;
    setSending(true);
    try {
      await api.post(`/parent-portal/messages-manage/${activeThread.id}/reply`, { body: replyBody });
      setReplyBody('');
      openThread(activeThread.id);
      toast.success('Réponse envoyée');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  const closeThread = async () => {
    if (!activeThread) return;
    try {
      await api.post(`/parent-portal/messages-manage/${activeThread.id}/close`);
      toast.success('Conversation fermée');
      setActiveThread(null);
      setMessages([]);
      loadThreads();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Messagerie parents</h1>
      <p className="text-sm text-gray-500 mb-4">Répondre aux messages des parents</p>

      <div className="flex gap-2 mb-4">
        {['open', 'closed', ''].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => { setFilter(s); setActiveThread(null); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === s ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {s === 'open' ? 'Ouvertes' : s === 'closed' ? 'Fermées' : 'Toutes'}
          </button>
        ))}
      </div>

      {activeThread ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => { setActiveThread(null); setMessages([]); }} className="text-gray-500">
                <FiArrowLeft size={18} />
              </button>
              <div>
                <h3 className="font-semibold">{activeThread.subject}</h3>
                <p className="text-xs text-gray-500">
                  {activeThread.parent_prenom} {activeThread.parent_nom} — {activeThread.parent_email}
                </p>
              </div>
            </div>
            {activeThread.status === 'open' && (
              <button onClick={closeThread} className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1">
                <FiX size={14} /> Fermer
              </button>
            )}
          </div>
          <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
            {messages.map((m) => {
              const isStaff = m.sender_role !== 'parent';
              return (
                <div key={m.id} className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                    isStaff ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-800'
                  }`}>
                    <p className="text-xs opacity-70 mb-1">{m.sender_prenom} {m.sender_nom} ({m.sender_role})</p>
                    <p className="whitespace-pre-wrap">{m.body}</p>
                    <p className="text-[10px] opacity-60 mt-1">
                      {m.created_at ? new Date(m.created_at).toLocaleString('fr-FR') : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {activeThread.status === 'open' && (
            <form onSubmit={sendReply} className="p-4 border-t flex gap-2">
              <input
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Votre réponse au parent..."
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
              <button type="submit" disabled={sending} className="px-4 py-2 bg-primary-600 text-white rounded-lg">
                <FiSend size={16} />
              </button>
            </form>
          )}
        </div>
      ) : loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : threads.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">Aucun message</div>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <button
              key={t.id}
              onClick={() => openThread(t.id)}
              className="w-full text-left bg-white rounded-lg border border-gray-200 p-4 hover:border-primary-200"
            >
              <div className="flex justify-between">
                <div>
                  <p className="font-medium text-gray-900">{t.subject}</p>
                  <p className="text-sm text-gray-500">{t.parent_prenom} {t.parent_nom}</p>
                  <p className="text-xs text-gray-400 truncate mt-1">{t.last_preview}</p>
                </div>
                {t.unread_count > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full h-fit">{t.unread_count}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentMessagesManagePage;
